/**
 * 機能権限指定画面
 * skill_supervisor と sales_supervisor が各権限を指定するための統合管理画面
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Autocomplete,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Settings, Add, Delete, Edit } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { extractErrorMessage } from '../../utils/errorHandler';
import RequiredFieldLabel from '../../components/Common/RequiredFieldLabel';

interface Employee {
  employee_id: string;
  name: string;
  department_name: string;
}

interface SkillPermission {
  permission_id: number;
  employee_id: string;
  employee_name: string;
  permission_types: string[];
  valid_from: string;
  valid_to: string | null;
  designated_by_name: string;
}

interface SalesPermission {
  permission_id: number;
  employee_id: string;
  employee_name: string;
  can_manage_accounts: boolean;
  can_manage_cases: boolean;
  can_view_cases: boolean;
  can_view_dashboard: boolean;
  can_view_kpi: boolean;
  valid_from: string;
  valid_to: string | null;
  designated_by_name: string;
}

// スキル権限タイプの定義
const SKILL_PERMISSION_TYPES = [
  { value: 'skill_map_viewer', label: 'スキルマップ閲覧' },
  { value: 'skill_master_editor', label: 'スキルマスタ編集' },
] as const;

// 営業権限タイプの定義
const SALES_PERMISSION_TYPES = [
  { value: 'can_manage_accounts', label: '取引先マスタ管理' },
  { value: 'can_manage_cases', label: '案件追加・変更' },
  { value: 'can_view_cases', label: '案件一覧・詳細閲覧' },
  { value: 'can_view_dashboard', label: 'ダッシュボード閲覧' },
  { value: 'can_view_kpi', label: 'KPI閲覧' },
] as const;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`permissions-tabpanel-${index}`}
      aria-labelledby={`permissions-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const FunctionalPermissionsPage: React.FC = () => {
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);

  // ユーザーの権限を確認
  const isSkillSupervisor = user?.roles?.includes('skill_supervisor') || false;
  const isSalesSupervisor = user?.roles?.includes('sales_supervisor') || false;

  // 初期タブ設定: 両方の権限がある場合は0、sales_supervisorのみの場合も0（タブが1つしかないため）

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // 権限がない場合
  if (!isSkillSupervisor && !isSalesSupervisor) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>
          機能権限指定
        </Typography>
        <Alert severity="error">
          この機能にアクセスする権限がありません。
          skill_supervisor または sales_supervisor ロールが必要です。
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        機能権限指定
      </Typography>

      <Paper>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="権限指定タブ">
            {isSkillSupervisor && (
              <Tab
                icon={<Settings />}
                iconPosition="start"
                label="スキル管理権限"
                id="permissions-tab-0"
                aria-controls="permissions-tabpanel-0"
              />
            )}
            {isSalesSupervisor && (
              <Tab
                icon={<Settings />}
                iconPosition="start"
                label="営業管理権限"
                id={`permissions-tab-${isSkillSupervisor ? 1 : 0}`}
                aria-controls={`permissions-tabpanel-${isSkillSupervisor ? 1 : 0}`}
              />
            )}
          </Tabs>
        </Box>

        {isSkillSupervisor && (
          <TabPanel value={currentTab} index={0}>
            <SkillPermissionsTab />
          </TabPanel>
        )}

        {isSalesSupervisor && (
          <TabPanel value={currentTab} index={isSkillSupervisor ? 1 : 0}>
            <SalesPermissionsTab />
          </TabPanel>
        )}
      </Paper>
    </Box>
  );
};

/**
 * スキル管理権限タブ
 */
const SkillPermissionsTab: React.FC = () => {
  const [permissions, setPermissions] = useState<SkillPermission[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<SkillPermission | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ダイアログ入力状態
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [validFrom, setValidFrom] = useState((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());
  const [validTo, setValidTo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPermissions();
    fetchEmployees();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/permission-designation/skill-permissions', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setPermissions(data.data || []);
      }
    } catch (error) {
      console.error('スキル権限取得エラー:', error);
      setError(extractErrorMessage(error, 'スキル権限の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/permission-designation/employees', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      if (!response.ok) {
        console.error('社員一覧取得失敗:', response.status);
        return;
      }
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setEmployees(data.data);
      }
    } catch (error) {
      console.error('社員一覧取得エラー:', error);
    }
  };

  const handleOpenDialog = () => {
    setSelectedEmployee(null);
    setSelectedPermissions([]);
    setValidFrom((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());
    setValidTo('');
    setRemarks('');
    setError('');
    setDialogOpen(true);
  };

  const handlePermissionToggle = (value: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
    );
  };

  const handleOpenEditDialog = (perm: SkillPermission) => {
    setEditingPermission(perm);
    setSelectedPermissions(perm.permission_types || []);
    setValidFrom(perm.valid_from?.split('T')[0] || (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());
    setValidTo(perm.valid_to?.split('T')[0] || '');
    setError('');
    setEditDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      setError('社員を選択してください');
      return;
    }
    if (selectedPermissions.length === 0) {
      setError('権限タイプを1つ以上選択してください');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const response = await fetch('/api/permission-designation/skill-permissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          employee_id: selectedEmployee.employee_id,
          permission_types: selectedPermissions,
          valid_from: validFrom,
          valid_to: validTo || null,
          remarks: remarks || null,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('権限を指定しました');
        setDialogOpen(false);
        fetchPermissions();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || '権限指定に失敗しました');
      }
    } catch (error) {
      setError(extractErrorMessage(error, '権限指定に失敗しました'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editingPermission) return;
    if (selectedPermissions.length === 0) {
      setError('権限タイプを1つ以上選択してください');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`/api/permission-designation/skill-permissions/${editingPermission.employee_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          permission_types: selectedPermissions,
          valid_from: validFrom,
          valid_to: validTo || null,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('権限を更新しました');
        setEditDialogOpen(false);
        setEditingPermission(null);
        fetchPermissions();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || '権限更新に失敗しました');
      }
    } catch (error) {
      setError(extractErrorMessage(error, '権限更新に失敗しました'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (permissionId: number) => {
    try {
      const response = await fetch(`/api/permission-designation/skill-permissions/${permissionId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('権限を解除しました');
        fetchPermissions();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || '権限解除に失敗しました');
      }
    } catch (error) {
      setError(extractErrorMessage(error, '権限解除に失敗しました'));
    }
    setConfirmDeleteId(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">スキル管理権限一覧</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenDialog}
        >
          権限指定
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>社員ID</TableCell>
              <TableCell>氏名</TableCell>
              <TableCell>権限タイプ</TableCell>
              <TableCell>有効期間</TableCell>
              <TableCell>指定者</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : permissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  権限が登録されていません
                </TableCell>
              </TableRow>
            ) : (
              permissions.map((perm) => (
                <TableRow key={perm.permission_id}>
                  <TableCell>{perm.employee_id}</TableCell>
                  <TableCell>{perm.employee_name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(perm.permission_types || []).map((pt) => (
                        <Chip
                          key={pt}
                          label={SKILL_PERMISSION_TYPES.find((t) => t.value === pt)?.label || pt}
                          color={pt === 'skill_master_editor' ? 'primary' : 'default'}
                          size="small"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {perm.valid_from?.split('T')[0]} 〜 {perm.valid_to ? perm.valid_to.split('T')[0] : '無期限'}
                  </TableCell>
                  <TableCell>{perm.designated_by_name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleOpenEditDialog(perm)}
                      >
                        編集
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => setConfirmDeleteId(perm.permission_id)}
                      >
                        解除
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* スキル権限指定ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>スキル権限指定</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <RequiredFieldLabel label="社員" />
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => `${option.employee_id} - ${option.name} (${option.department_name})`}
                value={selectedEmployee}
                onChange={(_, newValue) => setSelectedEmployee(newValue)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="社員を選択してください" />
                )}
                noOptionsText="社員が見つかりません"
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                権限タイプ（複数選択可）
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {SKILL_PERMISSION_TYPES.map((type) => {
                  const isSelected = selectedPermissions.includes(type.value);
                  return (
                    <Chip
                      key={type.value}
                      label={type.label}
                      onClick={() => handlePermissionToggle(type.value)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isSelected ? 'primary.dark' : 'action.hover'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box>
              <RequiredFieldLabel label="有効開始日" />
              <TextField
                fullWidth
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              label="有効終了日（空欄で無期限）"
              type="date"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="備考"
              multiline
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : '指定'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>スキル権限編集</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="社員"
              value={editingPermission ? `${editingPermission.employee_id} - ${editingPermission.employee_name}` : ''}
              disabled
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                権限タイプ（複数選択可）
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {SKILL_PERMISSION_TYPES.map((type) => {
                  const isSelected = selectedPermissions.includes(type.value);
                  return (
                    <Chip
                      key={type.value}
                      label={type.label}
                      onClick={() => handlePermissionToggle(type.value)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isSelected ? 'primary.dark' : 'action.hover'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box>
              <RequiredFieldLabel label="有効開始日" />
              <TextField
                fullWidth
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              label="有効終了日（空欄で無期限）"
              type="date"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : '更新'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={confirmDeleteId !== null} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>権限解除の確認</DialogTitle>
        <DialogContent>
          <Typography>この権限を解除してもよろしいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>キャンセル</Button>
          <Button color="error" variant="contained" onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}>
            解除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * 営業管理権限タブ
 */
const SalesPermissionsTab: React.FC = () => {
  const [permissions, setPermissions] = useState<SalesPermission[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<SalesPermission | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ダイアログ入力状態
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [validFrom, setValidFrom] = useState((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());
  const [validTo, setValidTo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPermissions();
    fetchEmployees();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/permission-designation/sales-permissions', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setPermissions(data.data || []);
      }
    } catch (error) {
      console.error('営業権限取得エラー:', error);
      setError(extractErrorMessage(error, '営業権限の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/permission-designation/employees', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      if (!response.ok) {
        console.error('社員一覧取得失敗:', response.status);
        return;
      }
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setEmployees(data.data);
      }
    } catch (error) {
      console.error('社員一覧取得エラー:', error);
    }
  };

  const handleOpenDialog = () => {
    setSelectedEmployee(null);
    setSelectedPermissions([]);
    setValidFrom((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());
    setValidTo('');
    setRemarks('');
    setError('');
    setDialogOpen(true);
  };

  const handlePermissionToggle = (permValue: string) => {
    setSelectedPermissions(prev =>
      prev.includes(permValue)
        ? prev.filter(p => p !== permValue)
        : [...prev, permValue]
    );
  };

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      setError('社員を選択してください');
      return;
    }

    if (selectedPermissions.length === 0) {
      setError('少なくとも1つの権限を選択してください');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const response = await fetch('/api/permission-designation/sales-permissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          employee_id: selectedEmployee.employee_id,
          can_manage_accounts: selectedPermissions.includes('can_manage_accounts'),
          can_manage_cases: selectedPermissions.includes('can_manage_cases'),
          can_view_cases: selectedPermissions.includes('can_view_cases'),
          can_view_dashboard: selectedPermissions.includes('can_view_dashboard'),
          can_view_kpi: selectedPermissions.includes('can_view_kpi'),
          valid_from: validFrom,
          valid_to: validTo || null,
          remarks: remarks || null,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('権限を指定しました');
        setDialogOpen(false);
        fetchPermissions();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || '権限指定に失敗しました');
      }
    } catch (error) {
      setError(extractErrorMessage(error, '権限指定に失敗しました'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (permissionId: number) => {
    try {
      const response = await fetch(`/api/permission-designation/sales-permissions/${permissionId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('権限を解除しました');
        fetchPermissions();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || '権限解除に失敗しました');
      }
    } catch (error) {
      setError(extractErrorMessage(error, '権限解除に失敗しました'));
    }
    setConfirmDeleteId(null);
  };

  const handleOpenEditDialog = (perm: SalesPermission) => {
    setEditingPermission(perm);
    const perms: string[] = [];
    if (perm.can_manage_accounts) perms.push('can_manage_accounts');
    if (perm.can_manage_cases) perms.push('can_manage_cases');
    if (perm.can_view_cases) perms.push('can_view_cases');
    if (perm.can_view_dashboard) perms.push('can_view_dashboard');
    if (perm.can_view_kpi) perms.push('can_view_kpi');
    setSelectedPermissions(perms);
    setValidFrom(perm.valid_from);
    setValidTo(perm.valid_to || '');
    setError('');
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editingPermission) return;

    if (selectedPermissions.length === 0) {
      setError('少なくとも1つの権限を選択してください');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`/api/permission-designation/sales-permissions/${editingPermission.permission_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          can_manage_accounts: selectedPermissions.includes('can_manage_accounts'),
          can_manage_cases: selectedPermissions.includes('can_manage_cases'),
          can_view_cases: selectedPermissions.includes('can_view_cases'),
          can_view_dashboard: selectedPermissions.includes('can_view_dashboard'),
          can_view_kpi: selectedPermissions.includes('can_view_kpi'),
          valid_from: validFrom,
          valid_to: validTo || null,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('権限を更新しました');
        setEditDialogOpen(false);
        setEditingPermission(null);
        fetchPermissions();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || '権限更新に失敗しました');
      }
    } catch (error) {
      setError(extractErrorMessage(error, '権限更新に失敗しました'));
    } finally {
      setSubmitting(false);
    }
  };

  // 権限をChipリストに変換
  const getPermissionChips = (perm: SalesPermission) => {
    const chips: { label: string; value: string }[] = [];
    if (perm.can_manage_accounts) chips.push({ label: '取引先マスタ管理', value: 'can_manage_accounts' });
    if (perm.can_manage_cases) chips.push({ label: '案件追加・変更', value: 'can_manage_cases' });
    if (perm.can_view_cases) chips.push({ label: '案件閲覧', value: 'can_view_cases' });
    if (perm.can_view_dashboard) chips.push({ label: 'ダッシュボード', value: 'can_view_dashboard' });
    if (perm.can_view_kpi) chips.push({ label: 'KPI', value: 'can_view_kpi' });
    return chips;
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">営業管理権限一覧</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenDialog}
        >
          権限指定
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>社員ID</TableCell>
              <TableCell>氏名</TableCell>
              <TableCell>権限</TableCell>
              <TableCell>有効期間</TableCell>
              <TableCell>指定者</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : permissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  権限が登録されていません
                </TableCell>
              </TableRow>
            ) : (
              permissions.map((perm) => (
                <TableRow key={perm.permission_id}>
                  <TableCell>{perm.employee_id}</TableCell>
                  <TableCell>{perm.employee_name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {getPermissionChips(perm).map((chip) => (
                        <Chip key={chip.value} label={chip.label} size="small" color="primary" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {perm.valid_from?.split('T')[0]} 〜 {perm.valid_to ? perm.valid_to.split('T')[0] : '無期限'}
                  </TableCell>
                  <TableCell>{perm.designated_by_name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleOpenEditDialog(perm)}
                      >
                        編集
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => setConfirmDeleteId(perm.permission_id)}
                      >
                        解除
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 営業権限指定ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>営業権限指定</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <RequiredFieldLabel label="社員" />
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => `${option.employee_id} - ${option.name} (${option.department_name})`}
                value={selectedEmployee}
                onChange={(_, newValue) => setSelectedEmployee(newValue)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="社員を選択してください" />
                )}
                noOptionsText="社員が見つかりません"
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                権限（複数選択可）
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {SALES_PERMISSION_TYPES.map((type) => {
                  const isSelected = selectedPermissions.includes(type.value);
                  return (
                    <Chip
                      key={type.value}
                      label={type.label}
                      onClick={() => handlePermissionToggle(type.value)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isSelected ? 'primary.dark' : 'action.hover'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box>
              <RequiredFieldLabel label="有効開始日" />
              <TextField
                fullWidth
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              label="有効終了日（空欄で無期限）"
              type="date"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="備考"
              multiline
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : '指定'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>営業権限編集</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="社員"
              value={editingPermission ? `${editingPermission.employee_id} - ${editingPermission.employee_name}` : ''}
              disabled
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                権限（複数選択可）
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {SALES_PERMISSION_TYPES.map((type) => {
                  const isSelected = selectedPermissions.includes(type.value);
                  return (
                    <Chip
                      key={type.value}
                      label={type.label}
                      onClick={() => handlePermissionToggle(type.value)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isSelected ? 'primary.dark' : 'action.hover'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box>
              <RequiredFieldLabel label="有効開始日" />
              <TextField
                fullWidth
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              label="有効終了日（空欄で無期限）"
              type="date"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : '更新'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={confirmDeleteId !== null} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>権限解除の確認</DialogTitle>
        <DialogContent>
          <Typography>この権限を解除してもよろしいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>キャンセル</Button>
          <Button color="error" variant="contained" onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}>
            解除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FunctionalPermissionsPage;
